import type { LoggableEmployee } from '@hygge/shared';

/**
 * The "Log for" picker of the Log Time dialog: whose hours this entry is.
 *
 * Its own module because the interesting part is not the markup but the rule
 * below — what happens to a chosen colleague when the project changes — and the
 * frontend's vitest runs in a node environment, so logic worth testing has to
 * live outside the component. [HW-864]
 */

/** The default: the entry is the caller's own, and `employeeId` is not sent. */
export const LOG_FOR_SELF = 'self';

export interface LogForOption {
  /** `LOG_FOR_SELF`, or an employee id. */
  value: string;
  label: string;
}

/**
 * `Myself` first, then everyone the server said this caller may log for,
 * ordered by the label actually rendered — the server sorts by first-then-last
 * name, which is the wrong order once the Name Display setting says otherwise.
 */
export function logForOptions(
  employees: readonly LoggableEmployee[],
  formatName: (employee: LoggableEmployee) => string,
): LogForOption[] {
  const others = employees
    .map((employee) => ({ value: employee.id, label: formatName(employee) }))
    .sort((a, b) => a.label.localeCompare(b.label));
  return [{ value: LOG_FOR_SELF, label: 'Myself' }, ...others];
}

/**
 * The selection to keep after the loggable list changes — which happens on
 * every project change, since the authority and the membership are both the
 * project's.
 *
 * A colleague who is not on the newly chosen project falls back to `Myself`
 * rather than staying selected. Keeping them would send the hours somewhere
 * nobody asked for: HR has the authority anywhere, so the server would file
 * them silently, and a manager of only the previous project would be refused
 * with a 403 they could not read a cause into.
 */
export function resolveLogFor(
  selected: string,
  employees: readonly LoggableEmployee[],
): string {
  if (selected === LOG_FOR_SELF) return LOG_FOR_SELF;
  return employees.some((employee) => employee.id === selected) ? selected : LOG_FOR_SELF;
}

/**
 * Whether the loaded list actually backs the current selection.
 *
 * `undefined` — the list for this project has not arrived — counts as NOT
 * confirmed, which is the whole point: while it is in flight the picker is
 * hidden, so a colleague still sitting in state is a name the screen is no
 * longer showing. Sending it would file somebody else's hours against a project
 * chosen after they were picked — accepted silently for HR, refused with an
 * unreadable 403 for a manager.
 */
export function isLogForConfirmed(
  selected: string,
  employees: readonly LoggableEmployee[] | undefined,
): boolean {
  if (selected === LOG_FOR_SELF) return true;
  return employees?.some((employee) => employee.id === selected) ?? false;
}

/** What `POST /time-entries` should carry: nothing at all when logging for yourself. */
export function logForPayload(selected: string): { employeeId?: string } {
  return selected === LOG_FOR_SELF ? {} : { employeeId: selected };
}
